import { queryOptions } from "@tanstack/react-query";
import {
  copyOrganizationWorkout,
  createOrganizationWorkout,
  getOrganizationWorkout,
  listOrganizationWorkouts,
  removeOrganizationWorkout,
  removeOrganizationWorkoutItemAlternate,
  setOrganizationWorkoutLabels,
} from "api-client";

import {
  type CardioAlternate,
  type CreateWorkoutInput,
  type MadeMainWorkoutItem,
  type UpdatedCardioItem,
  type WorkoutDetail,
  type WorkoutList,
  type WorkoutItemAlternateInput,
  type WorkoutListInput,
  type WorkoutsRepository,
} from "./WorkoutsRepository";

export function createOrganizationWorkoutsRepository(organizationId: string) {
  const rootKey = ["workouts", "organization", organizationId] as const;
  const repository = {
    listOptions(input: WorkoutListInput) {
      return queryOptions<WorkoutList>({
        queryKey: [...rootKey, "list", input],
        async queryFn() {
          const response = await listOrganizationWorkouts({
            path: { organizationId },
            query: {
              labelIds: input.labelIds,
              pageIndex: input.pageIndex,
              pageSize: input.pageSize,
              query: input.query,
            },
          });
          return response.data.data;
        },
      });
    },
    async createWorkout(input: CreateWorkoutInput) {
      const response = await createOrganizationWorkout({
        path: { organizationId },
        body: input,
      });
      return response.data.data;
    },
    async copyWorkout(workoutId: string) {
      const response = await copyOrganizationWorkout({
        path: { organizationId, workoutId },
      });
      return response.data.data;
    },
    detailOptions(workoutId: string) {
      return queryOptions<WorkoutDetail>({
        queryKey: [...rootKey, "detail", workoutId],
        async queryFn() {
          const response = await getOrganizationWorkout({
            path: { organizationId, workoutId },
          });
          return response.data.data;
        },
      });
    },
    async removeWorkout(workoutId: string) {
      return removeOrganizationWorkout({
        path: { organizationId, workoutId },
      });
    },
    async setWorkoutLabels(workoutId: string, labelIds: string[]) {
      return setOrganizationWorkoutLabels({
        path: { organizationId, workoutId },
        body: { labelIds },
      });
    },
    addCardioAlternate(): Promise<CardioAlternate> {
      throw new Error("Organizations do not support cardio alternates yet");
    },
    updateCardioItem(): Promise<UpdatedCardioItem> {
      throw new Error("Organizations do not support editing cardio items yet");
    },
    async removeWorkoutItemAlternate({
      workoutId: _workoutId,
      ...path
    }: WorkoutItemAlternateInput) {
      return removeOrganizationWorkoutItemAlternate({
        path: { organizationId, ...path },
      });
    },
    makeWorkoutItemAlternateMain(): Promise<MadeMainWorkoutItem> {
      throw new Error(
        "Organizations do not support making alternates main yet",
      );
    },
    reorderWorkoutItemAlternates(): Promise<unknown> {
      throw new Error("Organizations do not support reordering alternates yet");
    },
  };

  return repository satisfies WorkoutsRepository;
}
